import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Waves, Anchor, Compass, TrendingUp, ArrowRight, Sparkles, Check, Loader2, Mail, Lock, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import PestleResults from "@/components/PestleResults";
import { readWmToken } from "@/lib/wmToken";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "", email: "", website: "", personal_linkedin: "", business_linkedin: "",
  industry: "", challenge: "", consent_marketing: false, consent_processing: false,
  consent_ai_disclaimer: false,
};

export default function CatchTheWave() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [alreadyScanned, setAlreadyScanned] = useState(null);
  const [resending, setResending] = useState(false);
  const [member, setMember] = useState(null);

  useEffect(() => {
    const claims = readWmToken();
    if (claims) {
      setMember(claims);
      if (claims.e) setForm((f) => (f.email ? f : { ...f, email: claims.e }));
    }
  }, []);

  const isMember = !!member;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const words = form.challenge.trim().split(/\s+/).filter(Boolean).length;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_processing) { toast.error("Please agree to data processing to get your scan."); return; }
    if (!form.consent_ai_disclaimer) { toast.error("Please acknowledge the AI disclaimer to continue."); return; }
    if (words < 30) { toast.error("Your challenge needs at least 30 words so the AI can actually help."); return; }
    setLoading(true);
    setAlreadyScanned(null);
    try {
      const wm = (typeof window !== "undefined" && window.sessionStorage.getItem("weave_wm_token")) || null;
      const { data } = await axios.post(`${API}/catch-wave/submit`, { ...form, wm });
      setResult(data);
      toast.success("Your wave has been spotted. Results below ↓");
      setTimeout(() => document.getElementById("results")?.scrollIntoView({ behavior: "smooth" }), 200);
    } catch (err) {
      console.error(err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object" && detail.code === "already_scanned") {
        setAlreadyScanned({ ...detail, email: form.email });
        setTimeout(() => document.getElementById("already-scanned")?.scrollIntoView({ behavior: "smooth" }), 150);
      } else {
        const msg = typeof detail === "string" ? detail : "Something went sideways. Try again in a moment.";
        toast.error(msg);
      }
    } finally { setLoading(false); }
  };

  const resendPrevious = async () => {
    if (!alreadyScanned?.email) return;
    setResending(true);
    try {
      await axios.post(`${API}/catch-wave/resend`, { email: alreadyScanned.email });
      toast.success("Your previous scan is on its way to your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't resend right now — try again in a moment.");
    } finally { setResending(false); }
  };

  return (
    <div className="font-body">
      {/* NAV */}
      <nav data-testid="site-nav" className="w-full px-6 md:px-12 py-5 flex items-center justify-between bg-[var(--cream)] border-b-2 border-[var(--ink)]">
        <a href="https://www.wearetheweave.co.uk" className="flex items-center gap-2" data-testid="logo-link">
          <img src="https://customer-assets.emergentagent.com/job_pestle-light/artifacts/ps2e1slp_WEAVE-LOGO_2024.png" alt="The Weave" className="h-9 md:h-11 w-auto" />
        </a>
        <div className="hidden md:flex items-center gap-8 text-sm font-semibold">
          <a href="#how" className="hover:text-[var(--coral)]">How it works</a>
          <a href="#inside" className="hover:text-[var(--coral)]">What&apos;s inside</a>
          <a href="#club" className="hover:text-[var(--coral)]">The 1% Club</a>
          {!isMember && (
            <a href="https://awake.wearetheweave.co.uk/login" target="_blank" rel="noreferrer" className="hover:text-[var(--coral)]" data-testid="nav-login">Member login</a>
          )}
        </div>
        {isMember ? (
          <a href="https://awake.wearetheweave.co.uk/dashboard" target="_blank" rel="noreferrer" className="btn-coral text-sm inline-flex items-center gap-2" data-testid="nav-back-dashboard">
            <LayoutDashboard className="w-4 h-4"/> Back to dashboard
          </a>
        ) : (
          <a href="#scan" className="btn-coral text-sm" data-testid="nav-cta">Catch my wave</a>
        )}
      </nav>

      {/* MEMBER WELCOME STRIP */}
      {isMember && (
        <div className="bg-[var(--ink)] text-white py-3 px-6 md:px-12 flex flex-wrap items-center justify-between gap-3 border-b-2 border-[var(--sun)]" data-testid="member-banner">
          <div className="flex items-center gap-3 text-sm">
            <span className="text-[10px] tracking-[0.3em] font-bold text-[var(--sun)] bg-white/10 px-2 py-1 rounded-full">1% CLUB MEMBER</span>
            <span className="text-white/80">Welcome back{member?.e ? <>, <strong className="text-white">{member.e}</strong></> : ""}. Quarterly Deep Scan unlocks coming soon.</span>
          </div>
          <a href="https://awake.wearetheweave.co.uk/dashboard" target="_blank" rel="noreferrer" className="text-sm text-[var(--sun)] underline font-semibold whitespace-nowrap" data-testid="member-banner-link">← Back to your awake dashboard</a>
        </div>
      )}

      {/* HERO */}
      <header className="relative overflow-hidden wave-bg noise text-white">
        <div className="absolute inset-0 tile-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
          <span className="chip" data-testid="hero-chip"><Sparkles className="w-3.5 h-3.5 text-[var(--sun)]" /> {isMember ? "1% Club · Quarterly Deep Scan unlocks coming soon" : "Free from The 1% Club · PESTLE Light · AI-powered"}</span>
          <h1 className="font-display font-black text-white text-5xl md:text-7xl lg:text-8xl leading-[0.95] mt-6 max-w-5xl">
            Catch the <span className="italic text-[var(--sun)] wavey-underline">Wave</span><br/>
            before it crashes<br/>over you.
          </h1>
          <p className="mt-8 text-lg md:text-xl max-w-2xl text-white/85">
            A personalised horizon scan for founders who refuse to be caught off guard.
            Tell us your challenge — we&apos;ll send back a PESTLE Light map of the sector you&apos;re riding, in minutes.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 items-center">
            <a href="#scan" className="btn-coral inline-flex items-center gap-2" data-testid="hero-cta-primary">Get my free scan <ArrowRight className="w-4 h-4"/></a>
            <a href="#inside" className="text-white/90 underline underline-offset-4 font-semibold" data-testid="hero-cta-secondary">See what&apos;s inside →</a>
          </div>
          </div>

          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <img
              src="https://customer-assets.emergentagent.com/job_pestle-light/artifacts/06vbnxlv_Catch-the-wave-Black-Shaka-Vector-illustration-1175405766_1650x1650.jpg"
              alt="Catch the Wave shaka illustration"
              className="w-72 md:w-96 lg:w-full max-w-md floaty shaka-invert"
              data-testid="hero-shaka"
            />
          </div>

          <div className="lg:col-span-12 mt-4 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl">
            {[
              { k: "3 min", v: "to complete" },
              { k: "6 forces", v: "PESTLE mapped" },
              { k: "0 waffle", v: "just signal" },
              { k: "£0", v: "initial scan" },
            ].map((s) => (
              <div key={s.k} className="border-t-2 border-white/30 pt-4">
                <div className="font-display text-4xl md:text-5xl font-black text-[var(--sun)]">{s.k}</div>
                <div className="text-sm text-white/80 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* marquee */}
        <div className="relative bg-[var(--sun)] text-[var(--ink)] border-y-2 border-[var(--ink)] py-3 overflow-hidden">
          <div className="marquee whitespace-nowrap font-display font-black text-2xl">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex items-center gap-12 pr-12">
                <span>GROW YOUR CAPACITY</span><Waves className="w-5 h-5"/>
                <span>BUILD YOUR NETWORK</span><Waves className="w-5 h-5"/>
                <span>FIND YOUR CASH</span><Waves className="w-5 h-5"/>
                <span>RIDE THE WAVE</span><Waves className="w-5 h-5"/>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* CO-FOUNDER PROMISE */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-5">
          <div className="text-xs tracking-[0.3em] font-bold text-[var(--coral)]">THINK OF US AS</div>
          <h2 className="font-display font-black text-5xl md:text-6xl mt-3 leading-[1]">The co-founder<br/>to the founder.</h2>
        </div>
        <div className="md:col-span-7 space-y-5 text-lg text-[var(--ink)]/85">
          <p>On the journey beside you — looking for opportunities, growing capacity, emboldening networks, and finding sources of cash.</p>
          <p>Catch the Wave is the opening move. A free, personalised PESTLE Light scan so you can see the swell before it breaks.</p>
          <div className="flex flex-wrap gap-3 pt-4">
            {["Opportunity", "Capacity", "Network", "Cash"].map((t) => (
              <span key={t} className="px-4 py-2 bg-[var(--ink)] text-[var(--sun)] rounded-full font-bold text-sm">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-[var(--ink)] text-white py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-xs tracking-[0.3em] font-bold text-[var(--sun)]">HOW IT WORKS</div>
          <h2 className="font-display font-black text-5xl md:text-6xl mt-3 max-w-3xl">Three steps.<br/>One clearer horizon.</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {[
              { n: "01", icon: Compass, t: "Tell us your challenge", d: "Name, links and a 200-word honest read on what you&apos;re wrestling with right now." },
              { n: "02", icon: Sparkles, t: "AI scans the horizon", d: "We map the six PESTLE forces shaping your sector, and spot the one wave worth riding." },
              { n: "03", icon: Anchor, t: "You get the map", d: "Strengths, weaknesses and a next-step recommendation — on screen and in your inbox." },
            ].map((s) => (
              <div key={s.n} className="hover-lift p-8 rounded-3xl border-2 border-white/15 bg-white/5">
                <div className="flex items-center justify-between">
                  <span className="font-display font-black text-5xl text-[var(--sun)]">{s.n}</span>
                  <s.icon className="w-7 h-7 text-[var(--coral)]" />
                </div>
                <h3 className="font-display font-bold text-2xl mt-8">{s.t}</h3>
                <p className="mt-3 text-white/75" dangerouslySetInnerHTML={{ __html: s.d }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="scan" className="py-24 bg-[var(--cream)]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-xs tracking-[0.3em] font-bold text-[var(--coral)]">YOUR FREE SCAN</div>
          <h2 className="font-display font-black text-5xl md:text-6xl mt-3">Hand us the compass.</h2>
          <p className="mt-5 text-lg max-w-2xl text-[var(--ink)]/80">The more honest the input, the sharper the read. Takes about three minutes.</p>

          <form onSubmit={submit} className="mt-12 card-sand p-8 md:p-12 space-y-7" data-testid="catch-wave-form">
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Your name" required>
                <Input data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Jasmine Okafor" required />
              </Field>
              <Field label="Email" required>
                <Input data-testid="input-email" type="email" value={form.email} onChange={set("email")} placeholder="you@yourbusiness.co" required />
              </Field>
              <Field label="Business website" required>
                <Input data-testid="input-website" value={form.website} onChange={set("website")} placeholder="https://yourbusiness.co" required />
              </Field>
              <Field label="Industry / sector (optional)">
                <Input data-testid="input-industry" value={form.industry} onChange={set("industry")} placeholder="e.g. SaaS for hospitality" />
              </Field>
              <Field label="Your personal LinkedIn" required>
                <Input data-testid="input-personal-li" value={form.personal_linkedin} onChange={set("personal_linkedin")} placeholder="https://linkedin.com/in/you" required />
              </Field>
              <Field label="Business LinkedIn page" required>
                <Input data-testid="input-business-li" value={form.business_linkedin} onChange={set("business_linkedin")} placeholder="https://linkedin.com/company/..." required />
              </Field>
            </div>

            <Field label={`Your 200-word challenge ${words ? `· ${words} words` : ""}`} required>
              <Textarea data-testid="input-challenge" value={form.challenge} onChange={set("challenge")} rows={7}
                placeholder="What&apos;s the real thing keeping you up? Markets shifting, customers changing, team stretched, cash runway… give us the honest version."
                className="bg-white border-2 border-[var(--ink)]/20 focus-visible:ring-[var(--coral)]" required />
            </Field>

            <div className="space-y-4 pt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox data-testid="consent-processing" checked={form.consent_processing} onCheckedChange={(v) => set("consent_processing")(!!v)} className="mt-1" />
                <span className="text-sm text-[var(--ink)]/85">
                  I agree that The Weave may process the information I&apos;ve shared to generate my PESTLE Light scan, in line with the <a className="underline font-semibold" href="https://www.wearetheweave.co.uk/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>. (Required under UK GDPR.)
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox data-testid="consent-ai-disclaimer" checked={form.consent_ai_disclaimer} onCheckedChange={(v) => set("consent_ai_disclaimer")(!!v)} className="mt-1" />
                <span className="text-sm text-[var(--ink)]/85">
                  I understand this scan is AI-generated, is a starting point only, and is <strong>not</strong> professional, financial, legal or regulatory advice. I&apos;ve read the <a className="underline font-semibold" href="#disclaimer">disclaimer below</a>.
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox data-testid="consent-marketing" checked={form.consent_marketing} onCheckedChange={(v) => set("consent_marketing")(!!v)} className="mt-1" />
                <span className="text-sm text-[var(--ink)]/85">
                  Optional — send me occasional emails about The 1% Club, events and horizon insights. I can unsubscribe any time.
                </span>
              </label>
            </div>

            <div className="flex flex-wrap gap-4 items-center pt-4">
              <button type="submit" disabled={loading} className="btn-coral inline-flex items-center gap-2 disabled:opacity-60" data-testid="submit-scan">
                {loading ? (<><Loader2 className="w-4 h-4 animate-spin"/> Scanning the horizon…</>) : (<>Get my free scan <ArrowRight className="w-4 h-4"/></>)}
              </button>
              <span className="text-xs text-[var(--ink)]/60">No card. No spam. Just signal.</span>
            </div>
          </form>
        </div>
      </section>

      {/* ALREADY SCANNED STATE */}
      {alreadyScanned && (
        <section id="already-scanned" className="py-20 bg-[var(--sand)] border-y-2 border-[var(--ink)]">
          <div className="max-w-4xl mx-auto px-6 md:px-12" data-testid="already-scanned-panel">
            <div className="flex items-center gap-2 text-xs tracking-[0.3em] font-bold text-[var(--coral)]">
              <Compass className="w-4 h-4"/> YOU&apos;VE ALREADY CAUGHT YOUR WAVE
            </div>
            <h2 className="font-display font-black text-4xl md:text-5xl mt-3 leading-[1.05] text-[var(--ink)]">
              One free scan per founder. You&apos;ve had yours — and it&apos;s still on file.
            </h2>
            <div className="mt-7 rounded-3xl bg-white border-2 border-[var(--ink)] p-6 md:p-8">
              {alreadyScanned.previous_headline && (
                <>
                  <div className="text-[11px] tracking-widest font-bold text-[var(--ink)]/60">YOUR PREVIOUS HEADLINE</div>
                  <p className="font-display font-bold text-xl md:text-2xl mt-2 text-[var(--ink)]">&ldquo;{alreadyScanned.previous_headline}&rdquo;</p>
                </>
              )}
              {alreadyScanned.previous_date && (
                <p className="mt-3 text-sm text-[var(--ink)]/70">Scanned on {new Date(alreadyScanned.previous_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
              )}
            </div>

            <p className="mt-7 text-lg text-[var(--ink)]/85 max-w-2xl">
              Ready for the quarterly deep scan? <strong>The 1% Club</strong> unlocks trend analysis, a value-proposition review and AI-powered scenario mapping — every quarter.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              {isMember ? (
                <a href="https://awake.wearetheweave.co.uk/dashboard" target="_blank" rel="noreferrer" className="btn-coral inline-flex items-center gap-2" data-testid="already-back-dashboard">
                  <LayoutDashboard className="w-4 h-4"/> Back to your awake dashboard
                </a>
              ) : (
                <a href="https://awake.wearetheweave.co.uk/join" target="_blank" rel="noreferrer" className="btn-coral inline-flex items-center gap-2" data-testid="already-join-club">
                  Join The 1% Club <ArrowRight className="w-4 h-4"/>
                </a>
              )}
              <button onClick={resendPrevious} disabled={resending} className="btn-ghost inline-flex items-center gap-2 disabled:opacity-60" data-testid="already-resend">
                {resending ? (<><Loader2 className="w-4 h-4 animate-spin"/> Sending…</>) : (<><Mail className="w-4 h-4"/> Email me my previous scan</>)}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* RESULTS */}
      {result && (
        <section id="results" className="py-20 bg-[var(--sand)] border-y-2 border-[var(--ink)]">
          <PestleResults data={result} />
        </section>
      )}

      {/* WHAT'S INSIDE (UPSELL) */}
      <section id="inside" className="py-24 bg-[var(--teal-deep)] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="text-xs tracking-[0.3em] font-bold text-[var(--sun)]">THE LIGHT VS THE FULL</div>
            <h2 className="font-display font-black text-5xl md:text-6xl mt-3 leading-[1]">Free is a taste.<br/>Members get the meal.</h2>
            <p className="mt-6 text-white/80 text-lg">Catch the Wave (free) opens the door. The 1% Club quarterly scan walks you through it — with trend analysis, a value-proposition review and AI-powered scenario mapping.</p>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-5">
            {/* PESTLE Light card — always unlocked */}
            <div className="rounded-3xl border-2 border-white/20 p-7 bg-white/5 hover-lift">
              <div className="text-[11px] tracking-widest font-bold text-[var(--sun)]">Free · this page</div>
              <h3 className="font-display font-black text-3xl mt-2">PESTLE Light</h3>
              <ul className="mt-6 space-y-3">
                {["Sector snapshot", "6 forces, strengths & weaknesses", "Single biggest wave to catch"].map((i) => (
                  <li key={i} className="flex gap-3 text-white/90"><Check className="w-5 h-5 text-[var(--mint)] shrink-0 mt-0.5"/>{i}</li>
                ))}
              </ul>
            </div>

            {/* Quarterly Deep Scan card — gated */}
            <div className={`relative rounded-3xl border-2 ${isMember ? "border-[var(--sun)]" : "border-white/20"} p-7 bg-white/5 hover-lift overflow-hidden`} data-testid="deep-scan-card">
              <div className="text-[11px] tracking-widest font-bold text-[var(--sun)] flex items-center gap-2">
                {isMember ? "Your 1% Club tier · unlocked" : <><Lock className="w-3 h-3"/> 1% Club · £49 / mo</>}
              </div>
              <h3 className="font-display font-black text-3xl mt-2">Quarterly Deep Scan</h3>
              <ul className="mt-6 space-y-3">
                {["Trend analysis across 12 months", "Value proposition review", "AI scenario mapping", "Founder 1:1 debrief"].map((i) => (
                  <li key={i} className="flex gap-3 text-white/90"><Check className="w-5 h-5 text-[var(--mint)] shrink-0 mt-0.5"/>{i}</li>
                ))}
              </ul>
              <div className="mt-6 pt-5 border-t border-white/15">
                {isMember ? (
                  <div className="space-y-2">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--sun)]/20 border border-[var(--sun)]/40 text-[var(--sun)] text-xs font-bold tracking-wider" data-testid="deep-scan-coming-soon">COMING SOON · WAVE 2</div>
                    <p className="text-sm text-white/75">We&apos;re building this inside your awake dashboard. You&apos;ll get an email the moment it&apos;s ready.</p>
                  </div>
                ) : (
                  <a href="https://awake.wearetheweave.co.uk/join" target="_blank" rel="noreferrer" className="btn-coral inline-flex items-center gap-2 w-full justify-center" data-testid="deep-scan-unlock">
                    <Lock className="w-4 h-4"/> Unlock with the 1% Club
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 1% CLUB CTA */}
      <section id="club" className="py-24 bg-[var(--coral)] text-white relative overflow-hidden noise">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative">
          <TrendingUp className="w-10 h-10 mx-auto text-[var(--sun)]" />
          {isMember ? (
            <>
              <h2 className="font-display font-black text-5xl md:text-7xl mt-4 leading-[0.95]">You&apos;re in.<br/>Keep riding.</h2>
              <p className="mt-6 text-white/95 text-lg max-w-2xl mx-auto">Your free PESTLE Light scan is yours to refresh whenever the horizon shifts. The Quarterly Deep Scan unlocks inside your awake dashboard, very soon.</p>
              <div className="mt-10 flex flex-wrap gap-4 justify-center">
                <a href="https://awake.wearetheweave.co.uk/dashboard" target="_blank" rel="noreferrer" className="bg-[var(--ink)] text-white font-bold px-7 py-4 rounded-full hover:bg-black transition inline-flex items-center gap-2" data-testid="club-back-dashboard">
                  <LayoutDashboard className="w-4 h-4"/> Back to your awake dashboard
                </a>
                <a href="#scan" className="text-white/90 underline underline-offset-4 font-semibold self-center" data-testid="club-scan-cta">Run another PESTLE Light →</a>
              </div>
            </>
          ) : (
            <>
              <h2 className="font-display font-black text-5xl md:text-7xl mt-4 leading-[0.95]">Ready to stop reacting<br/>and start riding?</h2>
              <p className="mt-6 text-white/95 text-lg max-w-2xl mx-auto">The 1% Club is where founders get the co-founder they never had — capacity, network, cash, opportunity. £49 / month. Cancel anytime.</p>
              <div className="mt-10 flex flex-wrap gap-4 justify-center">
                <a href="https://awake.wearetheweave.co.uk/join" target="_blank" rel="noreferrer" className="bg-[var(--ink)] text-white font-bold px-7 py-4 rounded-full hover:bg-black transition" data-testid="club-cta">Join The 1% Club</a>
                <a href="https://www.wearetheweave.co.uk/community-choice" target="_blank" rel="noreferrer" className="btn-ghost bg-white/10 border-white text-white hover:bg-white hover:text-[var(--ink)]" data-testid="club-find-out-more">Not convinced? Find out more</a>
                <a href="#scan" className="text-white/90 underline underline-offset-4 font-semibold self-center" data-testid="club-scan-cta">Start with a free scan →</a>
              </div>
              <p className="mt-6 text-sm text-white/85">
                Already a member? <a href="https://awake.wearetheweave.co.uk/login" target="_blank" rel="noreferrer" className="underline font-semibold hover:text-[var(--sun)]" data-testid="club-login">Log in to your founding-member space →</a>
              </p>
            </>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[var(--ink)] text-white/80 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-8">
          <div id="disclaimer" className="rounded-2xl border border-white/15 bg-white/5 p-6 md:p-8 text-sm leading-relaxed text-white/75" data-testid="disclaimer">
            <div className="text-[11px] tracking-[0.3em] font-bold text-[var(--sun)] mb-3">ABOUT THIS SCAN</div>
            <p className="mb-3">
              Catch the Wave is a free, AI-generated PESTLE Light overview produced using large language models. It is intended as a starting point for strategic thinking, not as professional, financial, legal, investment or regulatory advice. The analysis may contain errors, omissions or outdated information, and the observations are general in nature — they do not take account of your specific circumstances.
            </p>
            <p className="mb-3">
              You should not rely on this scan as the sole basis for any business, financial or legal decision. Before acting, seek advice from a suitably qualified professional (accountant, solicitor, FCA-authorised adviser as appropriate).
            </p>
            <p>
              The Weave, its members and its partners accept no liability for any loss, damage or consequence — direct or indirect — arising from use of, or reliance on, this scan. Use of Catch the Wave is subject to our <a href="https://www.wearetheweave.co.uk/privacy-policy" target="_blank" rel="noreferrer" className="underline text-[var(--sun)]">Privacy Policy</a> and Terms (available at <a href="https://www.wearetheweave.co.uk" target="_blank" rel="noreferrer" className="underline text-[var(--sun)]">wearetheweave.co.uk</a>).
            </p>
          </div>
          <div className="flex flex-wrap gap-6 items-center justify-between">
            <img src="https://customer-assets.emergentagent.com/job_pestle-light/artifacts/ps2e1slp_WEAVE-LOGO_2024.png" alt="The Weave" className="h-9 w-auto bg-white rounded-md p-1.5" />
            <div className="text-sm">© {new Date().getFullYear()} The Weave · wearetheweave.co.uk · UK GDPR compliant</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="font-semibold text-[var(--ink)]">{label}{required && <span className="text-[var(--coral)] ml-1">*</span>}</Label>
      {children}
    </div>
  );
}
